import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { ReactComponent as IconFeedbackUp } from '../../../img/feedback-up.svg';
import { ReactComponent as IconFeedbackDown } from '../../../img/feedback-down.svg';
import baseApi from '../../../api/baseApi';
import { getStatisticsUrlForFeedback, getStatisticsImg } from '../../../utils';
import { STATISTICS } from '../../../constants';
import { ReactComponent as Icon } from '../../../img/close.svg';

const FeedbackBox = ({ entryRef, entryId, entryUid }) => {
    const { t } = useTranslation();
    const [openFeedback, setOpenFeedback] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    const [textForm, setTextForm] = useState('');
    const [statisticsUrl, setStatisticsUrl] = useState('');
    const [invalidStateOfTextArea, setInvalidStateOfTextArea] = useState(false);
    const [showFeedbackArea, setShowFeedbackArea] = useState(false);

    const handleStatistics = (vote) => setStatisticsUrl(getStatisticsUrlForFeedback(entryUid, vote));

    const handleYesClick = (e) => {
        e.preventDefault();
        handleStatistics(STATISTICS.voteYes);
        setShowFeedbackArea(true);
        setIsSent(true);
    };

    const handleNoClick = (e) => {
        e.preventDefault();
        setShowFeedbackArea(true);
        setOpenFeedback(true);
    };

    const handleOnChangeTextarea = (e) => setTextForm(e.target.value);

    const sendResponse = (e) => {
        e.preventDefault();
        if (!textForm || !textForm?.trim()?.length) {
            setInvalidStateOfTextArea(true);
            return;
        }

        setInvalidStateOfTextArea(false);
        if (!entryRef) {
            console.error(`${entryRef} is not valid`);
            return;
        }

        handleStatistics(STATISTICS.voteNo);
        baseApi
            .post('/feedback', {
                text: textForm,
                entryRef: entryRef
            })
            .catch(console.error);
        setIsSent(true);
    };

    const renderForm = () => (
        <form onSubmit={sendResponse} data-cy="feedback-form">
            <button
                type="button"
                onClick={handleYesClick}
                disabled={openFeedback}
                className="button button--with-left-icon btn-cta"
                data-cy="feedback-btn-yes"
            >
                <IconFeedbackUp className="icon feedback-box__icon" aria-hidden="true" focusable="false" />
                {t('ui.feedbackBox.button.yes')}
            </button>
            <button
                type="button"
                onClick={handleNoClick}
                className="button button--with-left-icon btn-cta"
                data-cy="feedback-btn-no"
            >
                <IconFeedbackDown className="icon feedback-box__icon" aria-hidden="true" focusable="false" />
                {t('ui.feedbackBox.button.no')}
            </button>
            {showFeedbackArea && (
                <div
                    className={classNames('feedback-box__feed', { 'feedback-box__feed--visible': openFeedback })}
                    data-cy="feedback-feed"
                >
                    <label
                        className="feedback-box__label"
                        htmlFor={`feed-${entryId}`}
                    >
                        {t('ui.feedbackBox.textareaLabel')}
                    </label>
                    <textarea
                        className={classNames('feedback-box__answer', { 'feedback-box__answer--error': invalidStateOfTextArea })}
                        id={`feed-${entryId}`}
                        value={textForm}
                        onChange={handleOnChangeTextarea}
                        data-cy="feedback-textarea"
                    />
                    {invalidStateOfTextArea && (
                        <div
                            className="feedback-box__error"
                            data-cy="feedback-error"
                        >
                            {t('ui.feedbackBox.required')}
                        </div>
                    )}
                    <button
                        className="button btn-cta"
                        type="submit"
                        data-cy="feedback-btn-submit"
                    >
                        {t('ui.feedbackBox.button.send')}
                    </button>
                </div>
            )}
        </form>
    );

    const header = !isSent && (
        <h2 className="feedback-box__heading" data-cy="feedback-header">
            {t('ui.feedbackBox.header')}
        </h2>
    );

    const content = isSent
        ? <>
            {getStatisticsImg(statisticsUrl)}
            <div className="feedback-box__close">
                <button className="button btn-icon" data-cy="feedback-btn-close" onClick={() => setIsClosed(true)} aria-label="close feedback" type="button">
                    <Icon className="icon icon--xs" aria-hidden="true" focusable="false" />
                </button>
            </div>
            <p className="feedback-box__info" data-cy="feedback-text">
                {t('ui.feedbackBox.text')}
            </p>
        </>
        : renderForm();

    return !isClosed && (
        <section data-cy="feedback-box" className="feedback-box">
            {header}
            {content}
        </section>
    );
};

export default FeedbackBox;
